import type { Place } from '@/lib/place-search';

// The city nearest to a position, for the "Near me" button (owner's approval,
// 2026-09-29). Pure: the position never leaves the browser, and the list of
// coordinates (lib/places/canada-coords.json, from GeoNames, CC BY 4.0) is
// fetched only when the button is pressed.

export type Coords = Record<string, [lat: number, lon: number]>;

/** Farther than this from every city of the list, and there is no answer. */
export const NEAR_LIMIT_KM = 60;
/** A bigger city this close to the nearest one is the better answer: downtown Montreal, not Montreal-Est. */
export const BIGGER_WITHIN_KM = 3;

export function distanceKm(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * rad;
  const dLon = (b[1] - a[1]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(h));
}

/**
 * The city of `places` nearest to `position`, taking a much bigger one that is
 * hardly farther; null when none is within NEAR_LIMIT_KM. `keyOf` is
 * placeKey() - the key the coordinates are filed under.
 */
export function nearestPlace(position: [number, number], places: Place[], coords: Coords, keyOf: (city: string, province: string) => string): Place | null {
  const found: Array<{ place: Place; km: number }> = [];
  for (const place of places) {
    if (place.kind !== 'city') continue;
    const c = coords[keyOf(place.name, place.province)];
    if (!c) continue;
    const km = distanceKm(position, c);
    if (km <= NEAR_LIMIT_KM) found.push({ place, km });
  }
  if (!found.length) return null;
  const nearest = Math.min(...found.map((f) => f.km));
  return found.filter((f) => f.km <= nearest + BIGGER_WITHIN_KM).sort((a, b) => b.place.population - a.place.population)[0].place;
}

export interface NearbyPlace {
  name: string;
  province: string;
  /** Distance to the middle of the city, rounded to 5 km: enough to choose a city, too coarse to say where a person is. */
  km: number;
}

/**
 * The cities of `places` within `radiusKm` of `position`, nearest first, those of 10,000 people or more (a list of hamlets is no help in
 * choosing where to look). Pure, like nearestPlace: the position stays in the browser.
 */
export function placesWithin(position: [number, number], places: Place[], coords: Coords, keyOf: (city: string, province: string) => string, radiusKm: number, limit = 12): NearbyPlace[] {
  const found: NearbyPlace[] = [];
  for (const place of places) {
    if (place.kind !== 'city' || place.population < 10_000) continue;
    const c = coords[keyOf(place.name, place.province)];
    if (!c) continue;
    const km = distanceKm(position, c);
    if (km <= radiusKm) found.push({ name: place.name, province: place.province, km: Math.round(km / 5) * 5 });
  }
  return found.sort((a, b) => a.km - b.km || a.name.localeCompare(b.name)).slice(0, limit);
}
